import { useState, useEffect } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { useParams } from "react-router-dom";
import { Form, Input } from 'antd';
import { RootState, getAccountUsernameFromPath } from '@share/utils';
import { IPasswordState, resetPassword } from '@share/store/slices';
import { push } from 'react-router-redux';

import img from "@assets/images/element/signin.svg";

interface IMapStateToProps {
  passwordStore: IPasswordState;
}

interface IMapDispatchToProps {
  resetPassword: (token: string, password: string, confirmPassword: string) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps, WrappedComponentProps { }

function ResetPasswordNewComponent(props: IProps) {
  const dispatch = useDispatch();
  const params: any = useParams();
  const [companyName, setCompanyName] = useState<string>();
  const [companyEmail, setCompanyEmail] = useState<string>();
  const { isSuccess, result } = props.passwordStore;

  useEffect(() => {
    if (isSuccess && result) {
      setCompanyName(result.companyName);
      setCompanyEmail(result.email);
    }
  }, [isSuccess])

  const onFinish = async (values: any) => {
    props.resetPassword(params.token, values.password, values.confirm);
  };

  const backToLogin = (event: any) => {
    event.preventDefault();
    const accountName = getAccountUsernameFromPath(props.history);
    dispatch(push(`/${accountName}/log-in`));
  };

  return (
    <section className="log-in">
      <div className="container h-100 d-flex px-0 px-sm-4">
        <div className="row justify-content-center align-items-center m-auto" style={{ width: '100%' }}>
          <div className="col-12">
            <div className="bg-mode shadow rounded-3 overflow-hidden">
              <div className="row g-0">
                <div className="col-lg-6 d-flex align-items-center order-2 order-lg-1">
                  <div className="p-3 p-lg-5">
                    <img src={img} alt="Login Side" />
                  </div>
                  <div className="vr opacity-1 d-none d-lg-block"></div>
                </div>

                <div className="col-lg-6 order-1">

                  {isSuccess ?
                    (<div className="p-4 p-sm-7">
                      <h1 className="mb-2 h3 text-success">Success!</h1>
                      <p className="mb-0">Your password setting has been completed successfully.</p>

                      <form className="mt-4 text-start">
                        <div className="mb-3 position-relative">
                          <p className="mb-1">Company name:</p>
                          <span className="h5 uppercase">{companyName}</span>
                        </div>
                        <div className="mb-3 position-relative">
                          <p className="mb-1">Your company email:</p>
                          <span className="h5 uppercase">{companyEmail}</span>
                        </div>
                        <div className="d-flex d-flex-row column-gap-lg-2 pt-3">
                          <div className="flex-grow-1">
                            <a href="#" onClick={(e) => backToLogin(e)} className="btn btn-primary w-100 mb-0">Back to login</a>
                          </div>
                        </div>
                      </form>
                    </div>)
                    :
                    (<div className="p-4 p-sm-7">
                      <h1 className="mb-2 h3"><FormattedMessage id="reset.password" /></h1>
                      <Form
                        name="reset_password"
                        className="reset-password-form mt-5"
                        layout="vertical"
                        onFinish={onFinish}
                      >
                        <Form.Item
                          name="password"
                          label={<FormattedMessage id="new.password" />}
                          rules={[
                            { required: true, message: <FormattedMessage id="please.input.your.password" /> },
                            { min: 5, message: <FormattedMessage id="password.must.be.minimum.characters" /> }
                          ]}
                        >
                          <Input.Password className='form-control form-control-lg' style={{ display: 'inline-flex' }} />
                        </Form.Item>

                        <Form.Item
                          name="confirm"
                          label={<FormattedMessage id="confirm.new.password" />}
                          rules={[
                            { required: true, message: <FormattedMessage id="please.confirm.your.password" /> },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error(props.intl.formatMessage({ id: 'the.two.passwords.that.you.entered.do.not.match' })));
                              }
                            })
                          ]}
                        >
                          <Input.Password className='form-control form-control-lg' style={{ display: 'inline-flex' }} />
                        </Form.Item>

                        <Form.Item className='mt-5'>
                          <button type="submit" disabled={isSuccess} className="btn btn-primary w-100 mb-0">
                            <FormattedMessage id="save.changes" />
                          </button>
                        </Form.Item>
                      </Form>
                    </div>)
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    passwordStore: state.passwordStore
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, undefined, Action>): IMapDispatchToProps => ({
  resetPassword: (token: string, password: string, confirmPassword: string) => {
    dispatch(resetPassword(token, password, confirmPassword));
  }
});

const ResetPasswordComponentRouter = withRouter(ResetPasswordNewComponent);

export const ResetPasswordNew = connect(mapStateToProps, mapDispatchToProps)(injectIntl(ResetPasswordComponentRouter));
